import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { queryExecutionRunStatusSummary } from '@/features/executionRuns/data/executionRuns';
import {
  buildStatusDeltaMap,
  buildStatusMetricCards,
  mapStatusOptionsToMetricOptions,
  resolveMetricTone,
  type MetricCardItem,
  type MetricCardOption,
} from '@/features/metrics/components/statusMetricCards';
import { buildSuiteCounts } from '@/features/quality/components/qualityStatusDataHelpers';
import { useProjectStatusPresentation } from '@/features/shared/hooks/useProjectStatusPresentation';
import {
  collapseExecutionStatusCounts,
  collapseStatusOptions,
} from '@/features/shared/utils/projectStatusPresentation';
import { suiteRunSummaryStatusOptions } from '@/shared/constants/statusOptions';
import { getStatusCopy } from '@/shared/copy/status';
import { queryKeys } from '@/shared/hooks';
import type { GridFilters } from '@/ui/composites/grid/gridFilters';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { ExecutionRunStatusSummaryDto, SuiteRunStatus } from '@flowqa/shared';

type UseExecutionRunStatusMetricsParams = {
  organisationId?: string | null;
  projectId?: string | null;
  baseFilters?: GridFilters;
  previousFilters?: GridFilters;
  enabled?: boolean;
};

const buildSummaryInput = (filters: GridFilters | undefined): GridQueryInput => ({
  page: 1,
  pageSize: 1,
  sorts: [],
  filters: filters ?? [],
});

export const useExecutionRunStatusMetrics = ({
  organisationId,
  projectId,
  baseFilters,
  previousFilters,
  enabled = true,
}: UseExecutionRunStatusMetricsParams): {
  executionRunMetricCards: MetricCardItem[];
  loading: boolean;
  counts: ReturnType<typeof buildSuiteCounts>;
  total: number | null;
  previousCounts: ReturnType<typeof buildSuiteCounts>;
  previousTotal: number | null;
} => {
  const { groupErroredWithFailed } = useProjectStatusPresentation();
  const summaryInput = useMemo(() => buildSummaryInput(baseFilters), [baseFilters]);
  const previousSummaryInput = useMemo(
    () => (previousFilters ? buildSummaryInput(previousFilters) : null),
    [previousFilters],
  );
  const summaryFiltersKey = useMemo(() => JSON.stringify(summaryInput.filters), [summaryInput]);
  const previousSummaryFiltersKey = useMemo(
    () => (previousSummaryInput ? JSON.stringify(previousSummaryInput.filters) : 'latest'),
    [previousSummaryInput],
  );

  const summaryQuery = useQuery<ExecutionRunStatusSummaryDto>({
    queryKey: queryKeys.executionRunSummary(
      projectId ?? null,
      organisationId ?? null,
      summaryFiltersKey,
    ),
    enabled,
    queryFn: async () => queryExecutionRunStatusSummary(summaryInput, organisationId ?? undefined),
    placeholderData: keepPreviousData,
  });

  const previousSummaryQuery = useQuery<ExecutionRunStatusSummaryDto>({
    queryKey: queryKeys.executionRunSummary(
      projectId ?? null,
      organisationId ?? null,
      previousSummaryFiltersKey,
    ),
    enabled: enabled && Boolean(previousSummaryInput),
    queryFn: async () =>
      queryExecutionRunStatusSummary(
        previousSummaryInput ?? summaryInput,
        organisationId ?? undefined,
      ),
    placeholderData: previousSummaryInput !== null ? keepPreviousData : undefined,
  });

  const summary = summaryQuery.data ?? null;
  const previousSummary = previousSummaryQuery.data ?? null;
  const suiteCounts = useMemo(
    () => collapseExecutionStatusCounts(buildSuiteCounts(summary), groupErroredWithFailed),
    [groupErroredWithFailed, summary],
  );
  const previousSuiteCounts = useMemo(
    () => collapseExecutionStatusCounts(buildSuiteCounts(previousSummary), groupErroredWithFailed),
    [groupErroredWithFailed, previousSummary],
  );
  const showDelta = Boolean(previousSummary);
  const metricCardsLoading =
    (summaryQuery.isFetching && summaryQuery.data === undefined && !summaryQuery.isError) ||
    (previousSummaryInput !== null &&
      previousSummaryQuery.isFetching &&
      previousSummaryQuery.data === undefined &&
      !previousSummaryQuery.isError);
  const deltas = useMemo(
    () => (showDelta ? buildStatusDeltaMap(suiteCounts, previousSuiteCounts) : undefined),
    [previousSuiteCounts, showDelta, suiteCounts],
  );

  const options = useMemo<Array<MetricCardOption<SuiteRunStatus>>>(
    () =>
      mapStatusOptionsToMetricOptions(
        collapseStatusOptions(suiteRunSummaryStatusOptions, groupErroredWithFailed),
        resolveMetricTone,
      ),
    [groupErroredWithFailed],
  );

  const executionRunMetricCards = useMemo(
    () =>
      buildStatusMetricCards({
        options,
        counts: suiteCounts,
        deltas,
        total: summary?.total ?? null,
        previousCounts: previousSuiteCounts,
        previousTotal: previousSummary?.total ?? null,
        loading: metricCardsLoading,
        showDelta,
        totalLabel: 'Execution Runs',
        emptyValueLabel: getStatusCopy('noData'),
        showEmptyValueWhenTotalZero: true,
        hideZeroCountStatuses: ['errored', 'untested'],
      }),
    [
      deltas,
      options,
      previousSuiteCounts,
      previousSummary?.total,
      showDelta,
      suiteCounts,
      summary?.total,
      metricCardsLoading,
    ],
  );

  return {
    executionRunMetricCards,
    loading: metricCardsLoading,
    counts: suiteCounts,
    total: summary?.total ?? null,
    previousCounts: previousSuiteCounts,
    previousTotal: previousSummary?.total ?? null,
  };
};
